import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  type DialogProps,
  DialogTitle,
  IconButton,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import type React from 'react';

export interface BaseModalProps extends Omit<DialogProps, 'title' | 'maxWidth' | 'onClose'> {
  open: boolean;
  onClose?: (() => void) | undefined;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  hideCloseButton?: boolean;
  actions?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  confirmColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  contentSx?: SxProps<Theme>;
  headerSx?: SxProps<Theme>;
}

export const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  hideCloseButton = false,
  actions,
  confirmText,
  cancelText = 'Cancel',
  onConfirm,
  confirmDisabled = false,
  confirmLoading = false,
  confirmColor = 'primary',
  contentSx,
  headerSx,
  ...dialogProps
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      {...dialogProps}
    >
      <DialogTitle sx={{ m: 0, p: 2, ...headerSx }}>
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
        {!hideCloseButton && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers sx={contentSx}>
        {children}
      </DialogContent>

      {(actions !== undefined || confirmText || onConfirm || !hideCloseButton) && (
        <DialogActions sx={{ p: 2 }}>
          {actions !== undefined ? (
            actions
          ) : (
            <>
              <Button onClick={onClose} color="inherit" disabled={confirmLoading}>
                {cancelText}
              </Button>
              {onConfirm && confirmText && (
                <Button
                  onClick={onConfirm}
                  variant="contained"
                  color={confirmColor}
                  disabled={confirmDisabled || confirmLoading}
                >
                  {confirmLoading ? 'Loading...' : confirmText}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BaseModal;
